/* global globalThis */
import { PROJECTION_OBSERVER_POLICY as policy } from '../policies/projectionObserver.policy.js';

/** Optional numeric lower-bound diagnostics. No timers, requests or attribution payloads. */
export function createProjectionLongTaskMonitor({ Observer = globalThis.PerformanceObserver,
  now = () => performance.now() } = {}) {
  const prefixes = ['handlerRpc', 'renderRpc'];
  const windows = new Map();
  const metrics = { longTaskSupported: 0, longTaskObserverActive: 0, longTaskDropped: 0 };
  for (const prefix of prefixes) {
    metrics[prefix + 'LongTaskObservedMsAtMax'] = 0;
    metrics[prefix + 'LongTaskSamplesAtMax'] = 0;
  }
  let observer = null, epoch = 0, recent = [], lastEnd = 0, since = 0;
  const drop = (count = 1) => { metrics.longTaskDropped = Math.min(65535, metrics.longTaskDropped + count); };
  const include = (prefix, window, task) => {
    const overlap = Math.max(0, Math.min(window.end, task.end) - Math.max(window.start, task.start));
    if (!overlap) return;
    window.overlap = Math.min(window.end - window.start, window.overlap + overlap);
    metrics[prefix + 'LongTaskObservedMsAtMax'] = Math.floor(window.overlap);
    metrics[prefix + 'LongTaskSamplesAtMax'] = Math.min(65535, metrics[prefix + 'LongTaskSamplesAtMax'] + 1);
  };
  const stop = () => {
    epoch++; metrics.longTaskObserverActive = 0;
    try { observer?.disconnect(); } catch { drop(); }
    observer = null; recent = []; windows.clear();
  };
  const receive = (entries, generation) => {
    if (generation !== epoch || !metrics.longTaskObserverActive) return;
    try {
      const at = now();
      if (!Number.isFinite(at) || !Array.isArray(entries)) { drop(); return; }
      if (entries.length > policy.longTaskCapacity) drop(entries.length - policy.longTaskCapacity);
      for (const entry of entries.slice(-policy.longTaskCapacity)) {
        const start = entry.startTime, duration = entry.duration, end = start + duration;
        if (!Number.isFinite(start) || start < 0 || !Number.isFinite(duration)
            || duration < policy.longTaskMinMs || duration > policy.longTaskMaxMs || end > at + 1) { drop(); continue; }
        if (end <= since || end <= lastEnd) continue;
        const task = { start: Math.max(start, lastEnd, since), end };
        lastEnd = end;
        for (const [prefix, window] of windows) include(prefix, window, task);
        recent.push(task);
      }
      recent = recent.filter(task => task.end >= at - policy.deadlineMs);
      if (recent.length > policy.longTaskCapacity) {
        drop(recent.length - policy.longTaskCapacity); recent = recent.slice(-policy.longTaskCapacity);
      }
    } catch { drop(); stop(); }
  };
  return Object.freeze({
    start() {
      if (metrics.longTaskObserverActive) return;
      try {
        if (typeof Observer !== 'function' || !Observer.supportedEntryTypes?.includes('longtask')) return;
        since = now(); if (!Number.isFinite(since)) return;
        lastEnd = since; recent = []; windows.clear(); const generation = ++epoch;
        observer = new Observer(list => {
          // Delivery may occur after the receipt callback that selected a window.
          try { receive(list.getEntries(), generation); } catch { if (generation === epoch) { drop(); stop(); } }
        });
        observer.observe({ type: 'longtask', buffered: false });
        metrics.longTaskSupported = 1; metrics.longTaskObserverActive = 1;
      } catch { drop(); stop(); }
    },
    track(prefix, start, end) {
      if (!prefixes.includes(prefix) || !Number.isFinite(start) || !Number.isFinite(end)
          || start < 0 || end < start || end - start > policy.deadlineMs) return;
      metrics[prefix + 'LongTaskObservedMsAtMax'] = 0; metrics[prefix + 'LongTaskSamplesAtMax'] = 0;
      if (!metrics.longTaskObserverActive) return;
      const window = { start, end, overlap: 0 }; windows.set(prefix, window);
      for (const task of recent) include(prefix, window, task);
    },
    stop,
    diagnostics: () => ({ ...metrics }),
  });
}
